import { Inject, Injectable, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
  HttpHeaders,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, finalize, tap } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { LogEvent } from '../enums/log-event';
import { FeatureMap } from 'src/app/shared/types/feature-map.type';
import { livechatPerformanceLogAdapter } from 'src/app/modules/live-chat/services/logger-adapters/livechat_performance_log.adapter';
import { MonitoringService } from '../services/monitoring.service';
import * as Sentry from '@sentry/angular-ivy';
import { APP_NAME_TOKEN } from '@core/constants/injection-tokens';
import { AttendanceChannel } from "../../modules/live-chat/interfaces/attendance";
import { BFF_ERROR_CODES } from '@core/constants/bff-error-messages';
import { isIgnoredForRequestLog } from '@core/constants/log-ignored-urls';

export function generateRequestTimeLogHeader(feature: FeatureMap) {
  return new HttpHeaders({ feature: feature });
}
export function generateRequestHeader(feature: FeatureMap, channel: AttendanceChannel) {
  return new HttpHeaders({ feature, channel });
}
@Injectable()
export class LogInterceptor implements HttpInterceptor {
  constructor(
    private logger: LoggerService,
    private monitoring: MonitoringService,
    private ngZone: NgZone,
    @Inject(APP_NAME_TOKEN) private appName: string,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let modifiedReq = this.removeFeatureHeader(request);
    modifiedReq = this.removeChannelHeader(modifiedReq);

    if (isIgnoredForRequestLog(request.url)) {
      return next.handle(modifiedReq);
    }

    const startTime = Date.now();
    let response: HttpErrorResponse | HttpResponse<any>;
    const feature: FeatureMap =
      (request.headers.get('feature') as FeatureMap) || 'unknown-feature';
    const channel =
      request.headers.get('channel') || 'unknown-channel';

    return next.handle(modifiedReq).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            response = event;
          }
        },
        error: (error: HttpErrorResponse) => {
          response = error;

          if (error.status === HttpStatusCode.NotModified) {
            return;
          }

          this.ngZone.runOutsideAngular(() => {
            Sentry.captureEvent({
              message: response?.url || error.message,
              transaction: error.name,
              level: 'error',
              extra: {
                httpBody: this.mapRequestValuesToObject(modifiedReq.body?.map),
                httpResponse: JSON.stringify(error, null, 4),
              },
            });
          });
        },
      }),
      finalize(() => {
        if (feature === 'unknown-feature' || !response) {
          this.logger.log(
            LogEvent.LIVECHAT_UNTRACKED_REQUEST_LOG,
            livechatPerformanceLogAdapter(
              modifiedReq,
              response,
              startTime,
              feature,
              this.monitoring,
              this.appName,
              channel
            )
          );
          return;
        }

        const exceptionCode = response.headers?.get('exception-code') || '';
        if (exceptionCode === BFF_ERROR_CODES.CONVERSATION_EXPIRED) {
          return;
        }

        this.logger.log(
          LogEvent.LIVECHAT_REQUEST_PERFORMANCE_LOG,
          livechatPerformanceLogAdapter(
            modifiedReq,
            response,
            startTime,
            feature,
            this.monitoring,
            this.appName,
            channel
          )
        );
      })
    );
  }
  removeFeatureHeader(originalRequest: HttpRequest<any>): HttpRequest<any> {
    if (originalRequest.headers.has('feature')) {
      const headers = originalRequest.headers.delete('feature');
      return originalRequest.clone({ headers });
    }
    return originalRequest;
  }
  removeChannelHeader(originalRequest: HttpRequest<any>): HttpRequest<any> {
    if (originalRequest.headers.has('channel')) {
      const headers = originalRequest.headers.delete('channel');
      return originalRequest.clone({ headers });
    }
    return originalRequest;
  }
  mapRequestValuesToObject(map: Map<string, string[]>): {
    [key: string]: string;
  } {
    const obj: { [key: string]: any } = {};
    try {
      for (const [key, value] of map.entries()) {
        const plainValue =
          Array.isArray(value) && value.length === 1 ? value[0] : value;
        obj[key] = plainValue;
      }
    } catch (e) {
      console.log('mapRequestValuesToObject() error', e);
    }
    return obj;
  }
}
